import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { API_BASE_URL as CONFIG_API_BASE_URL } from '../config/env'

// Environment-based API URL configuration
const API_BASE_URL = `${CONFIG_API_BASE_URL}/api`

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('trafix_admin_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('trafix_admin_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  token: string
  user: {
    id: string
    email: string
    fullName: string
    role: string
    isActive: boolean
  }
}

export interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalViolations: number
  pendingViolations: number
  approvedViolations: number
  usersByRole: Array<{ role: string; count: number }>
  violationsByType: Array<{ type: string; count: number }>
  recentViolations: Array<{
    id: string
    licensePlate: string
    violationType: string
    status: string
    reportedAt: string
    reporterName: string
  }>
}

export interface User {
  id: string
  email: string
  fullName: string
  phone?: string
  role: string
  isActive: boolean
  emailVerified: boolean
  phoneVerified: boolean
  createdAt: string
  lastLogin?: string
  profile?: {
    avatarUrl?: string
    city?: string
    district?: string
  }
}

export interface UsersResponse {
  users: User[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export interface ViolationReport {
  id: string
  licensePlate: string
  violationType: string
  status: string
  description: string
  reportedAt: string
  latitude: number
  longitude: number
  address: string
  city: string
  district: string
  photoUrl?: string
  rejectionReason?: string
  reporter: {
    id: string
    fullName: string
    email: string
  }
}

export interface ViolationsResponse {
  violations: ViolationReport[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export interface UpdateUserRequest {
  fullName?: string
  email?: string
  phone?: string
  role?: string
  isActive?: boolean
  password?: string
}

export interface CreateUserRequest {
  email: string
  fullName: string
  phone?: string
  role: string
  password: string
}

export interface UpdateViolationStatusRequest {
  status: string | number
  rejectionReason?: string
}

export interface SystemLog {
  id: string
  userId?: string
  userName?: string
  action: string
  entityType?: string
  entityId?: string
  oldData?: string
  newData?: string
  ipAddress?: string
  userAgent?: string
  createdAt: string
}

export interface SystemLogsResponse {
  logs: SystemLog[]
  pagination: {
    page: number
    pageSize: number
    hasMore: boolean
  }
}

export interface SystemLogStats {
  totalLogs: number
  actionStats: Array<{ action: string; count: number }>
  userStats: Array<{ userId: string; userName: string; count: number }>
  dateRange: {
    from?: string
    to?: string
  }
}

// Auth API
export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/Auth/admin/login', data)
    return response.data
  },

  logout: async (): Promise<void> => {
    await api.post('/Auth/logout')
  },

  getProfile: async () => {
    const response = await api.get('/Auth/profile')
    return response.data
  },

  updateProfile: async (data: { fullName: string; phone?: string }) => {
    const response = await api.put('/Auth/profile', data)
    return response.data
  },

  changePassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await api.post('/Auth/change-password', data)
    return response.data
  },
}

// Admin API
export const adminApi = {
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/Admin/dashboard')
    return response.data
  },

  getUsers: async (params?: {
    page?: number
    pageSize?: number
    search?: string
    role?: string
    isActive?: boolean
  }): Promise<UsersResponse> => {
    const response = await api.get('/Admin/users', { params })
    return response.data
  },

  getUser: async (id: string): Promise<User> => {
    const response = await api.get(`/Admin/users/${id}`)
    return response.data
  },

  createUser: async (data: CreateUserRequest): Promise<{ message: string; userId: string }> => {
    const response = await api.post('/Admin/users', data)
    return response.data
  },

  updateUser: async (id: string, data: UpdateUserRequest): Promise<void> => {
    await api.put(`/Admin/users/${id}`, data)
  },

  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/Admin/users/${id}`)
  },

  getViolations: async (params?: {
    page?: number
    pageSize?: number
    status?: string | number
    type?: string | number
    search?: string
  }): Promise<ViolationsResponse> => {
    // Filter out undefined values
    const cleanParams = Object.fromEntries(Object.entries(params || {}).filter(([, value]) => value !== undefined))



    const response = await api.get('/Admin/violations', { params: cleanParams })
    return response.data
  },

  getViolation: async (id: string): Promise<ViolationReport> => {
    const response = await api.get(`/Admin/violations/${id}`)
    return response.data
  },

  updateViolationStatus: async (id: string, data: UpdateViolationStatusRequest): Promise<void> => {
    await api.put(`/Admin/violations/${id}/status`, data)
  },

  getSystemLogs: async (params?: {
    page?: number
    pageSize?: number
    userId?: string
    action?: string
    fromDate?: string
    toDate?: string
  }): Promise<SystemLogsResponse> => {
    const response = await api.get('/SystemLogs', { params })
    return response.data
  },

  getSystemLogStats: async (params?: { fromDate?: string; toDate?: string }): Promise<SystemLogStats> => {
    const response = await api.get('/SystemLogs/stats', { params })
    return response.data
  },

  getSystemLogActions: async (): Promise<{ actions: string[] }> => {
    const response = await api.get('/SystemLogs/actions')
    return response.data
  },
}

export default api
